import React, { useState } from 'react';
import { 
  Settings as SettingsIcon,
  Bell,
  Shield,
  Wifi,
  Database,
  User,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: true,
      sms: false,
      alerts: true
    },
    thresholds: {
      humidity_min: 30,
      humidity_max: 60,
      temperature_min: 18,
      temperature_max: 25,
      battery_warning: 20
    },
    general: {
      refresh_interval: 30,
      data_retention: 90,
      timezone: 'Europe/Paris',
      language: 'fr'
    },
    security: {
      two_factor: false,
      api_access: true,
      auto_logout: 60
    }
  });

  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // Simuler la sauvegarde
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const tabs = [
    { id: 'general', name: 'Général', icon: SettingsIcon },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'thresholds', name: 'Seuils d\'alerte', icon: AlertTriangle },
    { id: 'security', name: 'Sécurité', icon: Shield },
    { id: 'data', name: 'Données', icon: Database }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Intervalle de rafraîchissement (secondes)
        </label>
        <select 
          value={settings.general.refresh_interval}
          onChange={(e) => updateSetting('general', 'refresh_interval', parseInt(e.target.value))}
          className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value={10}>10 secondes</option>
          <option value={30}>30 secondes</option>
          <option value={60}>1 minute</option>
          <option value={300}>5 minutes</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Rétention des données (jours)
        </label>
        <input
          type="number"
          min="30"
          max="365"
          value={settings.general.data_retention}
          onChange={(e) => updateSetting('general', 'data_retention', parseInt(e.target.value))}
          className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Fuseau horaire
        </label>
        <select 
          value={settings.general.timezone}
          onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
          className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="Europe/Paris">Europe/Paris</option>
          <option value="Europe/London">Europe/London</option>
          <option value="America/New_York">America/New_York</option>
          <option value="Asia/Tokyo">Asia/Tokyo</option>
        </select>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      {Object.entries(settings.notifications).map(([key, value]) => (
        <div key={key} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="font-medium text-slate-700 capitalize">
              {key === 'email' ? 'Notifications email' :
               key === 'push' ? 'Notifications push' :
               key === 'sms' ? 'Notifications SMS' :
               'Alertes système'}
            </p>
            <p className="text-sm text-slate-500">
              {key === 'email' ? 'Recevoir les alertes par email' :
               key === 'push' ? 'Notifications dans le navigateur' :
               key === 'sms' ? 'Recevoir les alertes par SMS' :
               'Afficher les alertes dans l\'interface'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => updateSetting('notifications', key, e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      ))}
    </div>
  );

  const renderThresholdSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Humidité minimale (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.thresholds.humidity_min}
            onChange={(e) => updateSetting('thresholds', 'humidity_min', parseInt(e.target.value))}
            className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Humidité maximale (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.thresholds.humidity_max}
            onChange={(e) => updateSetting('thresholds', 'humidity_max', parseInt(e.target.value))}
            className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Température minimale (°C)
          </label>
          <input
            type="number"
            min="-20"
            max="50"
            value={settings.thresholds.temperature_min}
            onChange={(e) => updateSetting('thresholds', 'temperature_min', parseInt(e.target.value))}
            className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Température maximale (°C)
          </label>
          <input
            type="number"
            min="-20"
            max="50"
            value={settings.thresholds.temperature_max}
            onChange={(e) => updateSetting('thresholds', 'temperature_max', parseInt(e.target.value))}
            className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Seuil d'alerte batterie (%)
        </label>
        <input
          type="number"
          min="5"
          max="50"
          value={settings.thresholds.battery_warning}
          onChange={(e) => updateSetting('thresholds', 'battery_warning', parseInt(e.target.value))}
          className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="text-sm text-slate-500 mt-1">
          Déclencher une alerte quand la batterie est en dessous de ce seuil
        </p>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
        <div>
          <p className="font-medium text-slate-700">Authentification à deux facteurs</p>
          <p className="text-sm text-slate-500">Sécuriser l'accès avec un code de vérification</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings.security.two_factor}
            onChange={(e) => updateSetting('security', 'two_factor', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>
      
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
        <div>
          <p className="font-medium text-slate-700">Accès API</p>
          <p className="text-sm text-slate-500">Permettre l'accès via API REST</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings.security.api_access}
            onChange={(e) => updateSetting('security', 'api_access', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Déconnexion automatique (minutes)
        </label>
        <select 
          value={settings.security.auto_logout}
          onChange={(e) => updateSetting('security', 'auto_logout', parseInt(e.target.value))}
          className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={60}>1 heure</option>
          <option value={120}>2 heures</option>
          <option value={0}>Jamais</option>
        </select>
      </div>
    </div>
  );

  const renderDataSettings = () => (
    <div className="space-y-6">
      <div className="p-6 border-2 border-dashed border-slate-300 rounded-xl text-center">
        <Database className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">Export des données</h3>
        <p className="text-slate-500 mb-4">
          Exportez toutes vos données de capteurs au format CSV ou JSON
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors duration-200">
            Exporter CSV
          </button>
          <button className="px-4 py-2 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-colors duration-200">
            Exporter JSON
          </button>
        </div>
      </div>
      
      <div className="p-6 border-2 border-dashed border-danger-300 rounded-xl text-center bg-danger-50">
        <AlertTriangle className="h-12 w-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-danger-700 mb-2">Zone de danger</h3>
        <p className="text-danger-600 mb-4">
          Cette action supprimera définitivement toutes les données historiques
        </p>
        <button className="px-4 py-2 bg-danger-600 text-white rounded-xl hover:bg-danger-700 transition-colors duration-200">
          Supprimer toutes les données
        </button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'thresholds':
        return renderThresholdSettings();
      case 'security':
        return renderSecuritySettings();
      case 'data':
        return renderDataSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient mb-2">
            Paramètres
          </h1>
          <p className="text-slate-600">
            Configurez votre système de monitoring des capteurs
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {saved && (
            <div className="flex items-center space-x-2 px-4 py-2 bg-success-100 text-success-700 rounded-xl border border-success-200">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Sauvegardé</span>
            </div>
          )}
          
          <button
            onClick={handleSave}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors duration-200"
          >
            <Save className="h-4 w-4" />
            <span>Sauvegarder</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="glass-effect rounded-2xl p-4">
            <div className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="glass-effect rounded-2xl p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h2>
              <p className="text-slate-600">
                {activeTab === 'general' && 'Paramètres généraux du système'}
                {activeTab === 'notifications' && 'Configurez les notifications et alertes'}
                {activeTab === 'thresholds' && 'Définissez les seuils d\'alerte pour vos capteurs'}
                {activeTab === 'security' && 'Paramètres de sécurité et d\'accès'}
                {activeTab === 'data' && 'Gestion et export des données'}
              </p>
            </div>
            
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;